import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import WizardProgress from './components/WizardProgress';
import SiteDetailsStep from './components/SiteDetailsStep';
import AddressStep from './components/AddressStep';
import ClientDetailsStep from './components/ClientDetailsStep';
import StatusAndComplianceStep from './components/StatusAndComplianceStep';
import ReviewStep from './components/ReviewStep';
import { siteService } from '../../services/siteService';
import { normalizeEnum } from '../../utils/normalizeEnum';
import { companyService } from '../../services/companyService';
import { activityService } from '../../services/activityService';
import useSubscriptionReadOnly from '../../hooks/useSubscriptionReadOnly';
import SubscriptionReadOnlyBanner from '../../components/SubscriptionReadOnlyBanner';
import Breadcrumb from '../../components/ui/Breadcrumb';
import BackButton from '../../components/ui/BackButton';
import { useAuth } from '../../contexts/AuthContext';
import { sanitizeError } from '../../utils/sanitizeError';


const SiteSetupWizard = () => {
  const navigate = useNavigate();
  const { isReadOnly: subscriptionReadOnly } = useSubscriptionReadOnly();
  const { isDemoAdmin } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLimitError, setShowLimitError] = useState(false);
  const [limitBlockedOnLoad, setLimitBlockedOnLoad] = useState(false);
  const [siteUsage, setSiteUsage] = useState(null);
  const [company, setCompany] = useState(null);

  const [formData, setFormData] = useState({
    siteName: '',
    siteReference: '',
    startDate: '',
    endDate: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    county: '',
    postcode: '',
    clientName: '',
    clientContactPerson: '',
    clientEmail: '',
    clientPhone: '',
    status: 'planning',
    acknowledgeCompliance: false
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadCompanyData();
  }, []);

  const loadCompanyData = async () => {
    try {
      const { data, error } = await companyService?.getUserCompany();
      if (error) throw error;
      if (data) {
        setCompanyId(data?.id);
        setCompany(data);

        // Upfront limit check — block wizard entry if already at limit
        const { data: usage } = await companyService?.getActiveSiteUsage(data?.id);
        if (usage) {
          setSiteUsage(usage);
          if (!usage?.can_add_more && !isDemoAdmin) {
            setLimitBlockedOnLoad(true);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load company:', err);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { id: 1, title: 'Site Details', component: SiteDetailsStep },
    { id: 2, title: 'Address', component: AddressStep },
    { id: 3, title: 'Client Info', component: ClientDetailsStep },
    { id: 4, title: 'Status', component: StatusAndComplianceStep },
    { id: 5, title: 'Review', component: ReviewStep }
  ];

  const validatePostcode = (postcode) => {
    const postcodeRegex = /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i;
    return postcodeRegex?.test(postcode?.trim());
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^(\+44\s?7\d{3}|\(?07\d{3}\)?)\s?\d{3}\s?\d{3}$|^(\+44\s?[1-9]\d{1,4}|\(?0[1-9]\d{1,4}\)?)\s?\d{3,4}\s?\d{3,4}$/;
    return phoneRegex?.test(phone?.replace(/\s/g, ''));
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData?.siteName?.trim()) {
          newErrors.siteName = 'Site name is required';
        }
        if (!formData?.startDate) {
          newErrors.startDate = 'Start date is required';
        }
        if (!formData?.endDate) {
          newErrors.endDate = 'End date is required';
        }
        if (formData?.startDate && formData?.endDate) {
          if (new Date(formData?.startDate) >= new Date(formData?.endDate)) {
            newErrors.endDate = 'End date must be after start date';
          }
        }
        break;

      case 2:
        if (!formData?.addressLine1?.trim()) {
          newErrors.addressLine1 = 'Address line 1 is required';
        }
        if (!formData?.city?.trim()) {
          newErrors.city = 'City is required';
        }
        if (!formData?.postcode?.trim()) {
          newErrors.postcode = 'Postcode is required';
        } else if (!validatePostcode(formData?.postcode)) {
          newErrors.postcode = 'Please enter a valid UK postcode';
        }
        break;

      case 3:
        if (!formData?.clientName?.trim()) {
          newErrors.clientName = 'Client name is required';
        }
        if (!formData?.clientContactPerson?.trim()) {
          newErrors.clientContactPerson = 'Contact person is required';
        }
        if (!formData?.clientEmail?.trim()) {
          newErrors.clientEmail = 'Client email is required';
        } else if (!validateEmail(formData?.clientEmail)) {
          newErrors.clientEmail = 'Please enter a valid email address';
        }
        if (!formData?.clientPhone?.trim()) {
          newErrors.clientPhone = 'Client phone is required';
        } else if (!validatePhoneNumber(formData?.clientPhone)) {
          newErrors.clientPhone = 'Please enter a valid UK phone number';
        }
        break;

      case 4:
        if (!formData?.status) {
          newErrors.status = 'Please select a site status';
        }
        if (!formData?.acknowledgeCompliance) {
          newErrors.acknowledgeCompliance = 'You must acknowledge the compliance requirements';
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e?.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps?.length));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    setIsSubmitting(true);
    setShowLimitError(false);

    try {
      const sitePayload = {
        companyId: companyId,
        siteName: formData?.siteName,
        siteReference: formData?.siteReference,
        addressLine1: formData?.addressLine1,
        addressLine2: formData?.addressLine2,
        city: formData?.city,
        county: formData?.county,
        postcode: formData?.postcode,
        clientName: formData?.clientName,
        clientContactPerson: formData?.clientContactPerson,
        clientEmail: formData?.clientEmail,
        clientPhone: formData?.clientPhone,
        // StatusAndComplianceStep emits 'on-hold' (hyphen) but the DB enum is
        // 'on_hold' (underscore). Normalise defensively — also covers any
        // future dropdown label drift like "On Hold" or " ON HOLD ".
        status: normalizeEnum(formData?.status),
        startDate: formData?.startDate,
        endDate: formData?.endDate
      };

      const { data: siteData, error: siteError } = await siteService?.createSite(sitePayload);

      if (siteError) {
        if (siteError?.message === 'SITE_LIMIT_REACHED') {
          navigate('/subscription-management');
          setIsSubmitting(false);
          return;
        }
        throw siteError;
      }

      if (!siteData?.id) {
        throw new Error('Site creation did not return a valid record. This may be a permissions issue — please contact support.');
      }

      await activityService?.createActivity({
        companyId: companyId,
        siteId: siteData?.id,
        activityType: 'site_created',
        title: 'New Site Created',
        description: `${formData?.siteName} site added to system`,
        relatedEntityId: siteData?.id,
        metadata: { siteId: siteData?.id, siteName: formData?.siteName, status: formData?.status }
      })?.catch(() => {}); // Activity log is non-critical

      navigate('/admin-dashboard');
    } catch (error) {
      console.error('[SiteSetupWizard] Site creation failed:', error);
      setErrors({ form: sanitizeError(error?.message, 'Failed to create site. Please try again.') });
      setIsSubmitting(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="flex flex-col items-center gap-4">
            <Icon name="Loader2" size={48} className="animate-spin text-accent" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Blocked at entry — navigate to subscription management
  if (limitBlockedOnLoad) {
    navigate('/subscription-management');
    return null;
  }

  const CurrentStepComponent = steps?.[currentStep - 1]?.component;
  const activeSiteCount = siteUsage?.active_count ?? siteUsage?.active_sites ?? 0;
  const maxSiteCount = siteUsage?.max_sites ?? 0;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-6 md:py-8 max-w-4xl">
        <div className="flex items-center justify-between gap-4 mb-2">
          <div className="flex items-center gap-4">
            <BackButton fallback="/admin-dashboard" />
            <Breadcrumb
              items={[
                { label: 'Dashboard', path: '/admin-dashboard' },
                { label: 'Add New Site', path: '/site-setup-wizard' }
              ]}
            />
          </div>
          {/* Site usage badge */}
          {siteUsage && maxSiteCount > 0 && (
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${
              !siteUsage?.can_add_more
                ? 'bg-destructive/10 border-destructive/20 text-destructive'
                : activeSiteCount >= maxSiteCount * 0.8
                ? 'bg-warning/10 border-warning/20 text-warning' :'bg-muted border-border text-muted-foreground'
            }`}>
              Active sites: {activeSiteCount} / {maxSiteCount} included
            </span>
          )}
        </div>

        <div className="bg-card rounded-lg shadow-elevation-2 border border-border p-4 md:p-8 mt-6">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground font-heading mb-2">
              Add New Site
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Complete all steps to set up a new construction site
            </p>
          </div>

          <WizardProgress steps={steps} currentStep={currentStep} totalSteps={steps?.length} onExit={() => navigate('/admin-dashboard')} />

          {errors?.form && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 text-destructive">
                <Icon name="AlertCircle" size={20} />
                <p className="font-medium">{errors?.form}</p>
              </div>
            </div>
          )}

          <div className="mb-8">
            <CurrentStepComponent
              formData={formData}
              errors={errors}
              handleInputChange={handleInputChange}
              handleCheckboxChange={handleCheckboxChange}
            />
          </div>

          <div className="flex flex-col-reverse md:flex-row gap-3 md:justify-between pt-6 border-t border-border">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1 || isSubmitting}
              className="w-full md:w-auto"
            >
              <Icon name="ChevronLeft" size={20} />
              Back
            </Button>

            {currentStep < steps?.length ? (
              <Button
                variant="default"
                onClick={handleNext}
                disabled={isSubmitting}
                className="w-full md:w-auto"
              >
                Next
                <Icon name="ChevronRight" size={20} />
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={handleSubmit}
                disabled={isSubmitting || subscriptionReadOnly}
                className="w-full md:w-auto"
              >
                {isSubmitting ? (
                  <>
                    <Icon name="Loader2" size={20} className="animate-spin" />
                    Creating Site...
                  </>
                ) : (
                  <>
                    <Icon name="Check" size={20} />
                    Create Site
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </main>

    </div>
  );
};

export default SiteSetupWizard;